import {
  formatCurrentOccupationFromAttributes,
  formatInterestedCareersFromAttributes,
  formatSkillsWantedFromAttributes,
  mapLearnerObjectEducationLevelToOption,
  mapLearnerObjectLearningGoalToOption,
} from 'bundles/onboarding-common/utils/savedQuestionDataUtils';
import type { PersonalizationSettingsAttributesQuery } from 'bundles/personalization-settings/queries/__generated__/PersonalizationSettingsAttributes';

/**
 * The value per Personalization field, in each field's raw editable shape --
 * the same shape its editor already seeds itself with, so every field can be
 * fed from this single page-level query instead of firing its own.
 */
export type PersonalizationFieldValues = {
  learningGoal: ReturnType<typeof mapLearnerObjectLearningGoalToOption>;
  desiredRole: ReturnType<typeof formatInterestedCareersFromAttributes>;
  skills: ReturnType<typeof formatSkillsWantedFromAttributes>;
  currentRole: ReturnType<typeof formatCurrentOccupationFromAttributes>;
  educationLevel: ReturnType<typeof mapLearnerObjectEducationLevelToOption>;
};

/**
 * Maps the explicit Learner Object attributes query into a per-field value
 * keyed by the scaffold's `LearnerObjectFieldId`. Fields with no explicit
 * value map to `null`.
 */
export const formatLearnerObjectAttributes = (
  data?: PersonalizationSettingsAttributesQuery
): PersonalizationFieldValues => {
  const attributes = data?.LearnerObject?.attributesByUserId;
  return {
    learningGoal: mapLearnerObjectLearningGoalToOption(attributes?.learningGoal),
    desiredRole: formatInterestedCareersFromAttributes(attributes?.interestedCareers?.careers),
    // Personalization settings always shows user-generated skills alongside
    // catalog ones (unlike other `filterUserGeneratedSkills` consumers).
    skills: formatSkillsWantedFromAttributes(attributes?.skillsWantedV2?.skills, { filterUserGeneratedSkills: false }),
    currentRole: formatCurrentOccupationFromAttributes(attributes?.currentOccupation),
    educationLevel: mapLearnerObjectEducationLevelToOption(attributes?.educationProfile?.educationLevel),
  };
};
